import {
  ICustomEmailContent,
  IEntity,
  IEntityAutoCollectExclusionMap,
  IEntityMap,
  IGroupAutoCollectExclusionMap,
  IGroupMap,
  IRole,
  ISelfBillBulkSubscription,
  UUID,
} from "src/app/core/interfaces";
import { convertTimestampsToDates } from "src/app/core/utils/helper-functions";
import { IOrganizationStateModel } from "./secure-pro-model.interface";
import { doc, getDoc, getFirestore, } from "@angular/fire/firestore";

export class OrganizationStateObject implements IOrganizationStateModel {
  id: string;
  name: string;
  orgPhone?: string | null;
  orgEmail?: string | null;
  groups: IGroupMap;
  entities?: IEntityMap;
  roles?: { [roleId: string]: IRole } | undefined;
  logoUrl?: string;
  stripeCustomerId?: string | null = null;
  selfBilled?: boolean | null = null;
  autoCollectOrgDefaultEnabled?: boolean | null = null;
  customEmailContent?: ICustomEmailContent | null = null;
  enterpriseFeaturesEnabled?: boolean | null = null;
  bulkPricingCheckoutType?: ISelfBillBulkSubscription | null = null;
  autoCollectEntityExclusions?: IEntityAutoCollectExclusionMap;
  autoCollectGroupExclusions?: IGroupAutoCollectExclusionMap;

  constructor(org: IOrganizationStateModel) {
    this.id = org.id;
    this.name = org.name;
    this.orgPhone = org.orgPhone;
    this.orgEmail = org.orgEmail;
    this.groups = org.groups ? convertTimestampsToDates(org.groups) : {};
    this.entities = convertTimestampsToDates(this.buildEntities(org));
    this.roles = org.roles;
    this.logoUrl = org.logoUrl;
    this.stripeCustomerId = org.stripeCustomerId;
    this.selfBilled = org.selfBilled;
    this.autoCollectOrgDefaultEnabled = org.autoCollectOrgDefaultEnabled;
    this.customEmailContent = org.customEmailContent;
    this.enterpriseFeaturesEnabled = org.enterpriseFeaturesEnabled;
    this.bulkPricingCheckoutType = org?.bulkPricingCheckoutType;
    this.autoCollectEntityExclusions = org.autoCollectEntityExclusions;
    this.autoCollectGroupExclusions = org.autoCollectGroupExclusions;
  }

  get activeGroups() {
    const activeGroups = Object.keys(this.groups).reduce((acc, groupId) => {
      const group = this.groups[groupId];
      if (group.active) {
        acc[groupId] = group;
      }
      return acc;
    }, {} as IGroupMap);

    return activeGroups;
  }

  /**
   * @description Returns the list of entities in the organization.
   * It flattens the entities object into an array and adds the group information to each entity.
   */
  get entitiesList(): IEntity[] {
    return Object.entries(this.entities || {}).map(([id, entity]) => {
      const groupName = Object.entries(this.groups).find(
        ([groupId]) => groupId === entity.groupId
      )?.[1]?.name;
      return {
        ...entity,
        groupName: groupName || "",
        id,
      };
    });
  }

  private buildEntities(org: IOrganizationStateModel): IEntityMap {
    const entities = {} as IEntityMap;

    Object.keys(org.entities || {}).map(async (entityId) => {
      if (!org.entities) {
        return;
      }
      const entity = org.entities[entityId];
      if (!entity) return;
      entities[entityId] = entity;
    });
    return entities;
  }

  getUserGroups(uid: UUID): UUID[] {
    const userGroups = Object.keys(this.activeGroups).reduce((acc, groupId) => {
      const group = this.activeGroups[groupId];
      if (group.userIds.includes(uid)) {
        acc.push(groupId);
      }
      return acc;
    }, [] as UUID[]);

    return userGroups;
  }

  async fetchEntityById(entityId: UUID): Promise<IEntity | undefined> {
    const db = getFirestore();
    const entityRef = doc(db, "organization", this.id, "entityList", entityId);
    const entitySnapshot = await getDoc(entityRef);
    const entity = entitySnapshot.data() as IEntity;
    return convertTimestampsToDates(entity);
  }
}
